// ---------- pointillist alphabet, three touch sizes ----------
//   0       empty cell = bare paper (the highlights)
//   1..27   nine pigments x three touch sizes (petit, moyen, grand)
//           code = (pigment index) * 3 + (size index) + 1
//
// POURQUOI IL EXISTE. `pointille` a deux plafonds, ni l'un ni l'autre un bug de tramage :
//
//   1. LA FORME. Un disque de rayon 0,46 ne couvre que π × 0,46² ≈ 0,665 de sa case — 33,5 % de
//      papier perce toujours, quelle que soit la teinte. `pointille.encode.ts` apparie contre
//      le pigment PUR, jamais contre ce que la case rend vraiment une fois le papier mêlé :
//      choisir une plus grosse touche referme une partie de l'écart, mais UNE seule taille pour
//      les neuf pigments écrase toute nuance de valeur qu'un pigment pourrait porter seul.
//   2. LE PLANCHER. Le pigment le plus sombre (outremer) a une luminance de 0,20 : aucune ombre
//      de la photo ne peut jamais descendre plus bas, seule ou mélangée optiquement à d'autres.
//
// Ce fichier attaque le premier plafond par un troisième axe : la TAILLE de la touche, en plus
// de sa teinte. Trois disques par pigment (0,28 / 0,46 — la taille actuelle — / 0,62), et
// l'appariement (dans .encode.ts) se fait contre la couleur EFFECTIVEMENT rendue de chaque
// combinaison teinte+taille — papier compris — et non contre le pigment pur. Une zone pâle
// prend alors un petit point de la bonne teinte plutôt qu'un grand point de la mauvaise, choisi
// faute de mieux. Mesuré sur la planche `essais/alphabets-tableaux/pointille.html` : l'écart
// perçu passe de 12,3 (aujourd'hui) à 7,7, devant même les disques uniformément agrandis (8,4).
//
// Toujours aucun noir ni gris — le principe divisionniste de `pointille.ts` tient tel quel, ce
// fichier n'y change rien.
//
// VINGT-SEPT CODES, AU-DELÀ DE L'ANCIEN PLAFOND NB_CODES (18) — relevé à 28 dans
// `alphabets/index.ts` pour cet alphabet précisément, un changement partagé par toute la
// maison (cf. le commentaire de NB_CODES). Identifiant neuf plutôt que `pointille` modifié en
// place : `getPalette()[code].css` n'a pas de garde-fou, et un vieux client face à un code au-
// delà de son tableau (10 aujourd'hui) lèverait un TypeError — le même plantage que `des` avant
// `desPlein`, pas le cas plus doux d'ASCII ou de Hachures.

import { rgbOf } from '../ink';
import type { Alphabet } from './types';

const PIGMENTS = [
  { css: '#f2ca35', name: 'jaune de cadmium' },
  { css: '#8cb62c', name: 'vert de vessie' },
  { css: '#2ca272', name: 'vert émeraude' },
  { css: '#2e8ec4', name: 'bleu céruléum' },
  { css: '#1f2f7a', name: 'outremer' },
  { css: '#4a2a6b', name: 'violet de cobalt' },
  { css: '#b23a63', name: 'laque de garance' },
  { css: '#d63f2e', name: 'vermillon' },
  { css: '#e8802c', name: 'orange de cadmium' },
];

/** Petit, moyen (la taille de `pointille`), grand — fraction du côté de la case. */
const SIZES = [0.28, 0.46, 0.62] as const;
const SIZE_NAME = ['petite touche', 'touche moyenne', 'grande touche'];

export interface EffectivePigment {
  r: number;
  g: number;
  b: number;
  css: string;
  name: string;
}

let effective: EffectivePigment[] | null = null;
let paperLum = 0;

function chan(col: string) {
  const [r, g, b] = rgbOf(col);
  return { r, g, b };
}

function pigmentOf(code: number) {
  return PIGMENTS[Math.floor((code - 1) / 3)];
}
function sizeOf(code: number) {
  return SIZES[(code - 1) % 3];
}

function drawRaw(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  code: number,
): void {
  const s = Math.min(w, h);
  ctx.fillStyle = pigmentOf(code).css;
  ctx.beginPath();
  ctx.arc(x + w / 2, y + h / 2, s * sizeOf(code), 0, 7);
  ctx.fill();
}

/**
 * Couverture EFFECTIVE de chaque combinaison teinte+taille — papier compris — pour que
 * l'encodeur apparie contre ce que la case rend vraiment plutôt que contre le pigment pur.
 * Rastérisée sur une plaque de 3x3 cases, comme `hachuresPleines.ts::measure()` le fait pour
 * son propre rehaut réservé : un disque qui laisse du papier percer autour de lui n'a pas de
 * formule fermée simple, et ça reste la même mesure quelle que soit la taille.
 */
function measure(): void {
  const S = 24;
  const n = 3;
  const c = document.createElement('canvas');
  c.width = c.height = S * n;
  const ctx = c.getContext('2d')!;
  const paperCss =
    getComputedStyle(document.documentElement).getPropertyValue('--paper').trim() || '#fbf6ec';
  const p = chan(paperCss);
  // Sous les disques le canvas est transparent : le papier vu dessous est assombri de 4 % par
  // le voile de .sheetwrap (cf. pointille.ts, même correction).
  const p0 = { r: p.r * 0.96, g: p.g * 0.96, b: p.b * 0.96 };

  const entries: EffectivePigment[] = [{ ...p0, css: 'transparent', name: 'papier nu' }];
  for (let code = 1; code <= PIGMENTS.length * SIZES.length; code++) {
    ctx.fillStyle = paperCss;
    ctx.fillRect(0, 0, S * n, S * n);
    for (let r = 0; r < n; r++)
      for (let cc = 0; cc < n; cc++) drawRaw(ctx, cc * S, r * S, S, S, code);
    const d = ctx.getImageData(0, 0, S * n, S * n).data;
    let R = 0;
    let G = 0;
    let B = 0;
    for (let i = 0; i < d.length; i += 4) {
      R += d[i];
      G += d[i + 1];
      B += d[i + 2];
    }
    const m = d.length / 4;
    entries.push({ r: R / m, g: G / m, b: B / m, css: pigmentOf(code).css, name: '' });
  }
  effective = entries;
  paperLum = 0.299 * p0.r + 0.587 * p0.g + 0.114 * p0.b;
}

function ready(): void {
  if (!effective) measure();
}

/** Les couleurs effectivement rendues (papier + 27 combinaisons), pour pointilleElargi.encode.ts. */
export function getEffectivePalette(): EffectivePigment[] {
  ready();
  return effective!;
}

export function paperLuminanceElargi(): number {
  ready();
  return paperLum;
}

export const pointilleElargi: Alphabet = {
  id: 'pointilleElargi',
  sheetMode: 'single',
  label: 'Pointillisme élargi',
  symbolName: 'touche',
  selfColored: true,
  helpText:
    'Divisionnisme, en trois grains : chaque pigment se pose en <b>petite, moyenne ou grande ' +
    "touche</b> selon la valeur qu'il faut porter, plutôt qu'une taille unique pour les neuf. " +
    'Toujours aucun noir ni gris — les valeurs sombres naissent du mélange optique.',

  aspect: () => 1,

  // Ordre de code = ordre de la palette : ni la teinte ni la taille ne portent d'échelle de
  // couverture à respecter (contrairement aux alphabets à trame), l'ordre naturel suffit.
  paletteOrder: Array.from({ length: PIGMENTS.length * SIZES.length }, (_, i) => i + 1),

  codeName: (code) => `${pigmentOf(code).name}, ${SIZE_NAME[(code - 1) % 3]}`,

  draw(ctx, x, y, w, h, code) {
    if (!code) return;
    drawRaw(ctx, x, y, w, h, code);
  },
};
